/**
 * Normalize spot `/me/*` payloads (flatten Decimal128 / ObjectId wrappers)
 * for profile order UIs.
 */

export function spotIdString(v) {
  if (v == null) return "";
  if (typeof v === "object" && v !== null && "$oid" in v) return String(v.$oid);
  return String(v);
}

export function spotDecimalString(v) {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v !== null && "$numberDecimal" in v) return String(v.$numberDecimal);
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string") return v;
  return String(v);
}

/** Decimal for display; blank or numeric zero → em dash (e.g. pending when fully filled). */
export function spotDecimalZeroAsDash(v) {
  const s = spotDecimalString(v);
  if (s === "") return "—";
  const n = Number(s);
  if (Number.isFinite(n) && n === 0) return "—";
  return s;
}

/** Returns ISO-ish string usable by `Date.parse` when possible. */
export function spotIsoNormalize(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v;
  if (typeof v !== "object" || v === null) return "";
  if ("$date" in v && v.$date != null) {
    if (typeof v.$date === "string") return v.$date;
    if (typeof v.$date === "number") return new Date(v.$date).toISOString();
    if (typeof v.$date === "object" && v.$date !== null && "$numberLong" in v.$date) {
      const n = Number(v.$date.$numberLong);
      if (Number.isFinite(n)) return new Date(n).toISOString();
    }
  }
  return "";
}

const QUOTE_SUFFIXES = ["USDT", "USDC", "BUSD", "FDUSD", "TUSD", "EUR", "BTC"];

/** `BNBUSDT` → `BNB/USDT` */
export function spotPairToDisplay(pair) {
  const s = String(pair || "").toUpperCase().replace(/\//g, "").replace(/\s+/g, "");
  if (!s) return "—";
  for (const q of QUOTE_SUFFIXES) {
    if (s.endsWith(q) && s.length > q.length) {
      const base = s.slice(0, -q.length);
      if (base) return `${base}/${q}`;
    }
  }
  return String(pair);
}

export function formatSpotActivityDateTime(iso) {
  const raw = spotIsoNormalize(iso) || (typeof iso === "string" ? iso : "");
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fmtSide(side) {
  const s = String(side || "").trim().toUpperCase();
  if (s === "BUY") return "Buy";
  if (s === "SELL") return "Sell";
  return side || "—";
}

const ACTIVE_CANCEL_STATUSES = ["NEW", "PARTIALLY_FILLED", "PENDING", "TRIGGERED", "LOCKED", "ACTIVATED"];

/** Human-readable spot order lifecycle label for history tables */
export function formatOrderHistoryStatus(status) {
  const s = String(status || "").trim();
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\w+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Same TDS cell rule as trade history: no/zero → em dash; else amount + `fee_asset`. */
export function formatSpotTdsRoleCell(tdsRaw, feeAssetRaw) {
  const tdsStr = spotDecimalString(tdsRaw);
  const tdsNum = tdsStr === "" || tdsStr === "—" ? NaN : Number(tdsStr);
  const hasTds = Number.isFinite(tdsNum) && tdsNum !== 0;
  const feeAssetCur =
    feeAssetRaw != null && String(feeAssetRaw).trim() !== "" ? String(feeAssetRaw).trim() : "";
  if (!hasTds) return "—";
  return feeAssetCur ? `${tdsStr} ${feeAssetCur}` : tdsStr;
}

/** Spot engine `type` → short label for mobile hero (e.g. `limit` → `Limit`). */
export function spotEngineTypeToUiLabel(t) {
  const k = String(t || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const map = {
    limit: "Limit",
    market: "Market",
    stop_limit: "Stop Limit",
    stop_market: "Stop Market",
    stop: "Stop",
    oco: "OCO",
  };
  if (map[k]) return map[k];
  if (!t) return "—";
  return String(t)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function mapMeOrderToOpenUiRow(raw) {
  const idStr = spotIdString(raw._id) || raw.client_order_id || "";
  const st = String(raw.status || "").toUpperCase();
  return {
    id: idStr,
    time: formatSpotActivityDateTime(raw.created_at),
    side: fmtSide(raw.side),
    pairs: spotPairToDisplay(raw.pair),
    price: spotDecimalString(raw.price) || "—",
    fillPrice: spotDecimalZeroAsDash(raw.avg_execution_price),
    orderQty: spotDecimalString(raw.quantity) || "—",
    pending: spotDecimalZeroAsDash(raw.remaining_quantity),
    filled: spotDecimalZeroAsDash(raw.filled_quantity),
    orderType: String(raw.type || "").toLowerCase(),
    orderTypeLabel: spotEngineTypeToUiLabel(raw.type),
    cancellable: ACTIVE_CANCEL_STATUSES.includes(st),
  };
}

/** Order History table + mobile lists (extends open row naming). */
export function mapMeOrderToHistoryUiRow(raw) {
  const base = mapMeOrderToOpenUiRow(raw);
  return {
    ...base,
    fillPrice: spotDecimalZeroAsDash(raw.avg_execution_price),
    orderQty: spotDecimalString(raw.quantity) || "—",
    pendingAmount: spotDecimalZeroAsDash(raw.remaining_quantity),
    filledBase: spotDecimalZeroAsDash(raw.filled_quantity),
    filledQuote: spotDecimalZeroAsDash(raw.executed_value),
    statusEngine: raw.status,
    statusDisplay: formatOrderHistoryStatus(raw.status),
    fee: "—",
    tdsRole: "—",
  };
}

/**
 * History API orders may nest fills in `executions[]`.
 * One UI row per fill when executions exist (multi-fill splits); otherwise one summarized row per order.
 */
export function flattenOrderHistoryApiItemsToUiRows(items) {
  const list = Array.isArray(items) ? items : [];
  const out = [];
  for (const raw of list) {
    const execs = Array.isArray(raw?.executions) ? raw.executions.filter(Boolean) : [];
    const oid = spotIdString(raw?._id) || String(raw?.client_order_id || "order");
    const pairs = spotPairToDisplay(raw?.pair);
    const sideLabel = fmtSide(raw?.side);
    const limitPrice = spotDecimalString(raw?.price) || "—";
    const orderQty = spotDecimalString(raw?.quantity) || "—";
    const pendingAmount = spotDecimalZeroAsDash(raw?.remaining_quantity);
    const statusDisplay = formatOrderHistoryStatus(raw?.status);
    const statusEngine = raw?.status;

    const orderTypeLabel = spotEngineTypeToUiLabel(raw?.type);

    if (execs.length === 0) {
      out.push({
        id: oid,
        time: formatSpotActivityDateTime(raw?.updated_at || raw?.created_at),
        side: sideLabel,
        pairs,
        price: limitPrice,
        fillPrice: spotDecimalZeroAsDash(raw?.avg_execution_price),
        orderQty,
        pendingAmount,
        filledBase: spotDecimalZeroAsDash(raw?.filled_quantity),
        filledQuote: spotDecimalZeroAsDash(raw?.executed_value),
        fee: "—",
        tdsRole: "—",
        statusEngine,
        statusDisplay,
        orderTypeLabel,
      });
      continue;
    }

    execs.forEach((ex, i) => {
      const tid = spotIdString(ex?.trade_id) || String(i);
      const feeStr = `${spotDecimalString(ex?.fee) || "—"}${ex?.fee_asset != null ? ` ${ex.fee_asset}` : ""}`.trim();
      out.push({
        id: `${oid}:${tid}`,
        time: formatSpotActivityDateTime(ex?.timestamp || raw?.updated_at || raw?.created_at),
        side: sideLabel,
        pairs,
        price: limitPrice,
        fillPrice: spotDecimalZeroAsDash(ex?.price),
        orderQty,
        pendingAmount,
        filledBase: spotDecimalZeroAsDash(ex?.quantity),
        filledQuote: spotDecimalZeroAsDash(ex?.quote_quantity),
        fee: feeStr,
        tdsRole: formatSpotTdsRoleCell(ex?.tds, ex?.fee_asset),
        statusEngine,
        statusDisplay,
        orderTypeLabel,
      });
    });
  }
  return out;
}

/** `/me/trades` expanded legs — trade history columns only. */
export function mapMeTradeUiRow(raw) {
  const side = String(raw.side || "").toUpperCase();
  const qa =
    raw.quote_asset != null
      ? String(raw.quote_asset).toUpperCase()
      : (() => {
          const p = String(raw.pair || "").toUpperCase();
          if (p.endsWith("USDT")) return "USDT";
          if (p.endsWith("USDC")) return "USDC";
          return "USDT";
        })();

  const feeStr = `${spotDecimalString(raw.fee) || "—"}${raw.fee_asset != null ? ` ${raw.fee_asset}` : ""}`.trim();

  const tdsRole = formatSpotTdsRoleCell(raw.tds, raw.fee_asset);

  return {
    id: spotIdString(raw._id) || `${spotIdString(raw.trade_id) || "trade"}:${side}`,
    date: formatSpotActivityDateTime(raw.executed_at),
    pair: spotPairToDisplay(raw.pair),
    sideLabel: fmtSide(side),
    sideBuy: side === "BUY",
    price: spotDecimalString(raw.price) || "—",
    filled: spotDecimalString(raw.quantity) || "—",
    fee: feeStr,
    tdsRole,
    total: `${spotDecimalString(raw.quote_quantity) || "—"} ${qa}`.trim(),
  };
}
